import { useRef, useState, useCallback, useEffect } from 'react';

interface UseCameraStreamOptions {
  initialFacingMode?: 'user' | 'environment';
  audio?: boolean;
  width?: number;
  height?: number;
}

const log = (...args: unknown[]) => console.log('[Camera]', ...args);

/** Permission already granted: no dialog, so a slow call means trouble. */
const GUM_TIMEOUT_GRANTED_MS = 15_000;
/** Permission may prompt: the participant's own thinking time counts. */
const GUM_TIMEOUT_PROMPT_MS = 60_000;

/**
 * getUserMedia with a ceiling.
 *
 * Re-acquiring the camera immediately after releasing it — exactly what a
 * retake does — can hang forever on mobile, never resolving OR rejecting.
 * startStream's `finally` then never runs, so `initializingRef` latches true
 * and every later attempt (including the manual retry button that appears
 * after 3s) returns null silently: a dead viewfinder with an inactive record
 * button and no error. Bounding the call turns that into a real, retryable
 * error message.
 */
const getUserMediaWithTimeout = (
  constraints: MediaStreamConstraints,
  ms: number,
): Promise<MediaStream> =>
  new Promise<MediaStream>((resolve, reject) => {
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      reject(new Error("Camera didn't respond. Close other apps using the camera, then try again."));
    }, ms);
    navigator.mediaDevices.getUserMedia(constraints).then(
      stream => {
        clearTimeout(timer);
        // Arrived after we gave up — release it rather than leak the camera.
        if (timedOut) { stream.getTracks().forEach(t => t.stop()); return; }
        resolve(stream);
      },
      err => { clearTimeout(timer); if (!timedOut) reject(err); },
    );
  });

export const useCameraStream = (options: UseCameraStreamOptions = {}) => {
  const {
    initialFacingMode = 'environment',
    audio = false,
    width = 1280,
    height = 720,
  } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const facingRef = useRef<'user' | 'environment'>(initialFacingMode);
  const mountedRef = useRef(true);
  const initializingRef = useRef(false);

  // Options stored in refs (stable across renders)
  const audioRef = useRef(audio);
  const widthRef = useRef(width);
  const heightRef = useRef(height);

  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(initialFacingMode);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Stop current stream completely and reset video element */
  const stopStream = useCallback(() => {
    log('stopStream');

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.load(); // Force-reset the element
    }

    setIsReady(false);
  }, []);

  /** Start camera stream. Returns the MediaStream on success, null on failure. */
  const startStream = useCallback(async (mode?: 'user' | 'environment'): Promise<MediaStream | null> => {
    // Prevent concurrent initialization (StrictMode double-mount, rapid taps)
    if (initializingRef.current) {
      log('Already initializing, skipping');
      return null;
    }

    initializingRef.current = true;
    const target = mode || facingRef.current;
    log('startStream:', target);

    // Always stop existing stream first
    stopStream();

    // Wait for hardware to fully release the camera
    await new Promise(resolve => setTimeout(resolve, 200));

    // Bail if unmounted during the wait
    if (!mountedRef.current) {
      log('Unmounted during init delay, aborting');
      initializingRef.current = false;
      return null;
    }

    try {
      // Pre-check permissions (not all browsers support this).
      // The denial must be thrown OUTSIDE this try — it used to be inside, so
      // the catch meant for "permissions API unsupported" swallowed it and we
      // fell through to a getUserMedia that could only fail or hang.
      let permState: PermissionState | 'unknown' = 'unknown';
      try {
        const perm = await navigator.permissions.query({ name: 'camera' as PermissionName });
        permState = perm.state;
        log('Permission:', perm.state);
      } catch { /* permissions API unsupported (Safari) — continue anyway */ }
      if (permState === 'denied') {
        throw new Error('Camera permission denied');
      }

      // A permission prompt blocks getUserMedia until the participant answers,
      // so only apply the short ceiling when permission is already granted.
      const gumMs = permState === 'granted' ? GUM_TIMEOUT_GRANTED_MS : GUM_TIMEOUT_PROMPT_MS;

      const baseVideo = {
        width: { ideal: widthRef.current },
        height: { ideal: heightRef.current },
      };

      let stream: MediaStream;

      try {
        // Try exact facing mode first (forces actual camera switch)
        stream = await getUserMediaWithTimeout({
          video: { facingMode: { exact: target }, ...baseVideo },
          audio: audioRef.current,
        }, gumMs);
      } catch {
        log('Exact facingMode failed, trying ideal');
        // Fallback: ideal (non-exact) for single-camera or desktop devices
        stream = await getUserMediaWithTimeout({
          video: { facingMode: target, ...baseVideo },
          audio: audioRef.current,
        }, gumMs);
      }

      // Bail if unmounted while awaiting getUserMedia
      if (!mountedRef.current) {
        log('Unmounted after getUserMedia, stopping stream');
        stream.getTracks().forEach(t => t.stop());
        initializingRef.current = false;
        return null;
      }

      log('Got stream:', stream.id);
      streamRef.current = stream;
      facingRef.current = target;

      const video = videoRef.current;
      if (!video) {
        throw new Error('Video element not available');
      }

      // Attach stream to video element
      video.srcObject = stream;

      // Wait for video metadata to be ready
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
          resolve(); // Resolve anyway — play() will fail if truly broken
        }, 5000);

        const onLoaded = () => {
          clearTimeout(timeout);
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
          log('loadedmetadata fired');
          resolve();
        };

        const onError = () => {
          clearTimeout(timeout);
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
          reject(new Error('Video element error'));
        };

        video.addEventListener('loadedmetadata', onLoaded);
        video.addEventListener('error', onError);

        // If metadata already loaded (readyState >= 1), resolve immediately
        if (video.readyState >= 1) {
          clearTimeout(timeout);
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
          resolve();
        }
      });

      // Bail if unmounted during metadata wait
      if (!mountedRef.current) {
        log('Unmounted during metadata wait');
        stream.getTracks().forEach(t => t.stop());
        initializingRef.current = false;
        return null;
      }

      await video.play();
      log('Video playing');

      setFacingMode(target);
      setIsReady(true);
      setError(null);

      return stream;
    } catch (err) {
      log('Error:', err);
      setError(err instanceof Error ? err.message : 'Camera failed');
      setIsReady(false);
      return null;
    } finally {
      initializingRef.current = false;
    }
  }, [stopStream]);

  /** Toggle between front and back camera */
  const toggleCamera = useCallback(async (): Promise<MediaStream | null> => {
    const next = facingRef.current === 'environment' ? 'user' : 'environment';
    log('Toggle:', facingRef.current, '->', next);
    return startStream(next);
  }, [startStream]);

  /** Track mount state and cleanup on unmount */
  useEffect(() => {
    mountedRef.current = true;
    log('Hook mounted');

    return () => {
      log('Hook unmounting');
      mountedRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return {
    videoRef,
    streamRef,
    facingMode,
    isReady,
    error,
    startStream,
    stopStream,
    toggleCamera,
  };
};
